import { DEFAULT_PRACTICE_TIMEZONE } from "./practiceTimezone";

const normalizeInboundOptionalText = (value) =>
  String(value || "").trim() || "Not provided";

export const normalizeFollowUpReason = (followUpReason) =>
  normalizeInboundOptionalText(followUpReason);

export const normalizeTransferNumber = (transferNumber) =>
  String(transferNumber || "").trim() || "-";

// Shared by Inbound's and Summary's CSV exports so a phone number is masked to its last 4
// digits the same way in both places.
export const maskPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");

  if (!digits) {
    return "Not provided";
  }

  return `******${digits.slice(-4)}`;
};

export const enrichInboundCallRow = (row) => {
  return {
    ...row,
    ClinicNumber: row?.ClinicNumber || "Not provided",
    // BEGIN: Task 2 - Care Console uses TransferNumber + FollowUpReason only
    // REVERT RULE ("revert followup reason column"):
    // restore the previous inbound normalization and column shape again.
    TransferNumber: normalizeTransferNumber(row?.TransferNumber),
    FollowUpReason: normalizeFollowUpReason(row?.FollowUpReason),
    // END: Task 2 - Care Console uses TransferNumber + FollowUpReason only
  };
};

export const enrichInboundCallRows = (rows) =>
  Array.isArray(rows) ? rows.map(enrichInboundCallRow) : [];

export const safeDate = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getLatestCallAttemptDate = (row) => {
  const calls = Array.isArray(row?.Calls) ? row.Calls : [];
  return calls.reduce((latest, entry) => {
    const entryDate = safeDate(
      entry?.startedAt || entry?.CreateDateTime || entry?.createDateTime,
    );
    if (!entryDate) return latest;
    return !latest || entryDate > latest ? entryDate : latest;
  }, null);
};

// Prefers the most recent real call attempt (row.Calls[], sourced live from
// TwilioLogs) over the lead row's own CreateDateTime, which is frozen at
// first-ever contact and never advances for returning callers.
export const getLatestInboundCallDate = (row) =>
  getLatestCallAttemptDate(row) ||
  safeDate(
    row?.CreateDateTime ||
      row?.createDateTime ||
      row?.createdAt ||
      row?.StartedAt ||
      row?.startDateTime ||
      row?.date,
  );

// Alias - the logic above is direction-agnostic (any row.Calls[] array shaped by
// groupingCallLogs, inbound or outbound); Outbound's LeadGGrid reuses it under this name
// so its own "most recent call, not lead-created date" fix doesn't read as inbound-only.
export const getLatestLeadCallDate = getLatestInboundCallDate;

// buildLatestCallDateInRangeMap: row.Id -> the latest call attempt date that falls inside
// [startDateKey, endDateKey] ("YYYY-MM-DD", inclusive both ends, compared by calendar day in
// `timeZone`) - only rows with at least one matching attempt get an entry. A row can carry
// dozens of real call attempts (seen up to ~90 for a single patient), so this computes the
// whole page in one pass with a single reused date formatter, instead of re-parsing/reformatting
// every attempt on every grid cell render (DataGrid's valueGetter/renderCell can both run many
// times per render, and a fresh Intl.DateTimeFormat + full re-scan per cell measurably slowed
// the table down once rows like that were on the page).
export const buildLatestCallDateInRangeMap = (rows, startDateKey, endDateKey, timeZone = DEFAULT_PRACTICE_TIMEZONE) => {
  const map = new Map();
  if (!startDateKey || !endDateKey) return map;

  const dayFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row?.Id === undefined || row?.Id === null) return;
    const calls = Array.isArray(row?.Calls) ? row.Calls : [];
    let latest = null;
    calls.forEach((entry) => {
      const entryDate = safeDate(entry?.startedAt || entry?.CreateDateTime || entry?.createDateTime);
      if (!entryDate) return;
      const dayKey = dayFormatter.format(entryDate);
      if (dayKey < startDateKey || dayKey > endDateKey) return;
      if (!latest || entryDate > latest) latest = entryDate;
    });
    if (latest) map.set(row.Id, latest);
  });

  return map;
};

// buildCallStatsInRangeMap: row.Id -> { attempts, answered, missed, voicemail } counted only from
// calls that fall inside [startDateKey, endDateKey] - same range/timezone rules as
// buildLatestCallDateInRangeMap above. Without this, a grid's inline "X attempts / Y answered"
// label (LeadGGrid.jsx's Call Details column) and its "Missed Call"/"Voicemail" columns kept
// showing the lead's ALL-TIME TotalCallAttempts/CallReceived/MissedCalls/VoicemailCalls even
// while a date filter was active, disagreeing with everything else on the row that's already
// correctly scoped to the selected range. "answered"/"voicemail"/"missed" match the backend's own
// CallReceived/VoicemailCalls/MissedCalls definitions (services/index.js) - answered = completed
// only, voicemail counted separately, missed = neither.
export const buildCallStatsInRangeMap = (rows, startDateKey, endDateKey, timeZone = DEFAULT_PRACTICE_TIMEZONE) => {
  const map = new Map();
  if (!startDateKey || !endDateKey) return map;

  const dayFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row?.Id === undefined || row?.Id === null) return;
    const calls = Array.isArray(row?.Calls) ? row.Calls : [];
    let attempts = 0;
    let answered = 0;
    let voicemail = 0;
    calls.forEach((entry) => {
      const entryDate = safeDate(entry?.startedAt || entry?.CreateDateTime || entry?.createDateTime);
      if (!entryDate) return;
      const dayKey = dayFormatter.format(entryDate);
      if (dayKey < startDateKey || dayKey > endDateKey) return;
      attempts += 1;
      if (entry?.status === "completed") answered += 1;
      else if (entry?.status === "voicemail") voicemail += 1;
    });
    const missed = Math.max(0, attempts - answered - voicemail);
    map.set(row.Id, { attempts, answered, missed, voicemail });
  });

  return map;
};
